import { Menu, Transition, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import clsx from 'clsx';
import { Fragment } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import Typography from '~/shared/components/Typography';

const transitionProps = {
  enter: 'transition ease-out duration-100',
  enterFrom: 'transform opacity-0 scale-95',
  enterTo: 'transform opacity-100 scale-100',
  leave: 'transition ease-in duration-75',
  leaveFrom: 'transform opacity-100 scale-100',
  leaveTo: 'transform opacity-0 scale-95',
};

interface SimpleDropdownProps {
  items: string[];
  selectedItem: string;
  onSelect: (item: string) => void;
  align?: 'top-right' | 'top-left';
  className?: string;
  menuButtonClassName?: string;
  menuItemClassName?: string;
  disabled?: boolean;
  'aria-label'?: string;
}

export default function SimpleDropdown({
  items,
  selectedItem,
  onSelect,
  align = 'top-right',
  className,
  menuButtonClassName,
  menuItemClassName,
  disabled = false,
  'aria-label': ariaLabel,
}: SimpleDropdownProps) {
  return (
    <div className={clsx('relative', className)}>
      <Menu as="div" className="relative flex">
        {({ open }) => (
          <>
            <MenuButton
              disabled={disabled}
              aria-label={ariaLabel}
              className={clsx(
                'bg-white text-primary-900 border border-gray-300 flex justify-between rounded-[8px] px-[16px] h-[44px] text-[14px] w-full',
                disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                !disabled && open
                  ? 'bg-primary-20 border-primary-200'
                  : !disabled && 'hover:border-primary-500 active:border-primary-100',
                'focus:border-primary-300 focus:outline-none',
                menuButtonClassName
              )}
            >
              <div className="flex items-center justify-between w-full">
                <Typography variant="p3-reg" color="text-gray-700">
                  {selectedItem}
                </Typography>

                <ChevronDownIcon
                  className={clsx(
                    'w-[14px] h-[14px] text-gray-400 stroke-3 cursor-pointer',
                    open ? 'rotate-180' : ''
                  )}
                />
              </div>
            </MenuButton>

            <Transition as={Fragment} {...transitionProps}>
              <MenuItems
                className={clsx(
                  'absolute top-[100%] rounded-lg mt-2 origin-top-right w-full bg-white',
                  'shadow-filter-dropdown focus-outline-none border-primary-70',
                  'border border-primary-70 focus:outline-hidden z-30',
                  align === 'top-right' ? 'right-0 origin-top-right' : 'left-0 origin-top-left'
                )}
              >
                <div className="p3">
                  {items.map((item) => (
                    <MenuItem
                      as={'button'}
                      key={item}
                      className={clsx(
                        'text-sm leading-6 w-full h-12 flex items-center justify-between px-5 py-[18px] border-gray-200 last:rounded-b-lg cursor-pointer hover:bg-primary-20 group',
                        items[0] === item && 'rounded-t-lg',
                        item === selectedItem && 'bg-primary-20 border border-primary-500',
                        item !== selectedItem &&
                          item !== items[items.length - 1] &&
                          'border-b border-gray-200',
                        menuItemClassName
                      )}
                      onClick={() => {
                        onSelect?.(item);
                      }}
                    >
                      <Typography color="text-primary-900" variant="p3-reg">
                        {item}
                      </Typography>
                    </MenuItem>
                  ))}
                </div>
              </MenuItems>
            </Transition>
          </>
        )}
      </Menu>
    </div>
  );
}
